// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Khipu player (Khipu plan, slice 1;
 * `docs/AYNI_XR_KHIPU_REPLACES_TOURS_PLAN.md`): a panel at the top start
 * corner that plays a story, a briefing or a record on the globe.
 *
 * - **A path** (`khipuPlayback.planStep`): each step loads its dataset (a
 *   stream outside the plan shows its name with a lock), moves the camera
 *   (or frames its region), sets the date (or the first or latest frame),
 *   the playback, the overlays, the colours and the pins it decides, and
 *   shows its captions, questions, quiz, pictures, clips and links. Previous
 *   / Next move by hand; Play runs the steps that move on by themselves,
 *   Pause holds them. A quiz takes one answer and then shows the right one;
 *   in a meeting the presenter can put it to the room as a question.
 * - **A session record's log** (`khipuPlayback.replayMoments`): the events
 *   replayed in order with the meeting's waits shortened; the scrubber
 *   jumps to any moment and puts that moment's state on the globe at once.
 *
 * Links open in a new tab; nothing is embedded from another site. A lesson
 * is a canvas and opens in the lesson player instead (`lessonPlayerUI`).
 * The panel is dragged by its title bar like the lesson panel
 * (`floatingPanel`). In a meeting, the room follows what the presenter's
 * globe shows, as with a lesson, and sees the step's title, captions and
 * pins in a card of its own (`roomNoteUI`); the panel is the presenter's.
 *
 * Written from scratch for AYNI (owner, 2026-10-05): nothing here comes
 * from TerraViz's code.
 */
import { getLocale, t } from '../i18n'
import type { LiveStop } from './liveMotion'
import { formatDate } from '../i18n/format'
import type { Khipu, KhipuStep, KhipuView } from '../services/khipu'
import type { KhipuSatellites } from '../services/khipu'
import type { GlobeTimeRule } from '../services/globeSlots'
import type { RoomNote } from '../services/roomProtocol'
import { localized } from '../services/khipu'
import { khipuLoops } from '../services/khipuCollect'
import {
  createAdvanceClock,
  hasTimedSteps,
  planStep,
  playsLog,
  replayMoments,
  replayStateAt,
  type ReplayMoment,
  type StepDisplay,
  type StepRtLook,
  type StepOverlayPlan,
  type StepPlan,
  type StepQuiz,
  type StepRegion,
} from '../services/khipuPlayback'
import { saveKhipu } from '../services/khipuStore'
import { ensureLibraryDataset } from '../services/lessonLibrary'
import { resolveDatasetKey, zipKhipu } from '../services/lessonService'
import { decisionPromptOf } from '../services/situationTemplates'
import { escapeAttr, escapeHtml } from './domUtils'
import { isPanelShown, makeFloating } from './floatingPanel'
import type { VrKhipuState } from '../services/vrKhipuPanel'

/** AYNI Studio: a data move into a step's dataset, live (KhipuPlayerHost.moveData). */
export interface LiveDataMove {
  data: 'crossfade' | 'wipe' | 'basemap'
  wipe: 'meridian' | 'terminator'
  seconds: number
  /** The step's date, for the incoming dataset; null for its first frame. */
  atMs: number | null
  /** Where the step looks: a wipe is measured from there. */
  view: { lat: number; lon: number }
}

/** A step's data move, when it has one the host can play: a timed crossfade, wipe or fade through the basemap with a camera. */
export function liveDataMoveOf(p: StepPlan): LiveDataMove | null {
  const tr = p.motion?.transition
  const data = tr?.data
  if (!tr || !p.view || (data !== 'crossfade' && data !== 'wipe' && data !== 'basemap')) return null
  const seconds = typeof tr.seconds === 'number' ? tr.seconds : 1.5
  if (seconds <= 0) return null
  const atMs = p.time?.at ? Date.parse(p.time.at) : NaN
  return { data, wipe: tr.wipe ?? 'meridian', seconds, atMs: Number.isFinite(atMs) ? atMs : null, view: { lat: p.view.lat, lon: p.view.lon } }
}

export interface KhipuPlayerHost {
  /** Load a dataset the way the browse panel does; true once it is on the globe. */
  loadDataset(id: string): Promise<boolean>
  /**
   * AYNI Studio (Khipu 1.1): change to this dataset with a data move — the
   * new one drawn over the globe while the camera arrives (a crossfade, a
   * wipe), or the old one faded out and the new one in (through the
   * basemap) — `load` putting it up for good at the end. Stops (false)
   * once `current` is false. Without it the dataset just loads.
   */
  moveData?(id: string, move: LiveDataMove, load: () => Promise<boolean>, current: () => boolean): Promise<boolean>
  currentDatasetId(): string | null
  /** The package a stream needs that the plan lacks, by name, or null. */
  streamLock(id: string): Promise<string | null>
  /** Take the dataset off the globe: the bare Earth. */
  clearDataset?(): void
  setView(view: KhipuView): void
  /** Khipu 1.1: arrive at a step and hold it, with its effects, live (`ui/liveMotion.ts`); absent: setView. */
  playMotion?(stop: LiveStop): void
  /** End the step's motion and take its effects away. */
  stopMotion?(): void
  /** Khipu 1.2: the Satellites layer as the step sets it (`satellites/khipuSatellites.ts`); absent: left as it is. */
  setSatellites?(s: KhipuSatellites): void
  /** "Make a video of this Khipu": the Studio opens with it (plans with render credits); absent: not offered. */
  film?(k: Khipu): void
  /** Frame a box on the globe (a step with a region and no camera). */
  fitRegion?(region: StepRegion): void
  seekToDate(iso: string): void
  /** Go to the dataset's first or latest frame. */
  seekToEdge?(edge: 'first' | 'latest'): void
  /** Go to a point in the dataset's video, in seconds. */
  seekToOffset?(seconds: number): void
  /** Spin the globe (at `speed` times the default) or stop it. */
  setAutorotate?(on: boolean, speed?: number): void
  /** AYNI (2026-10-19): show the step on the flat 2:1 map or the globe; resolves once switched (the switch sets a camera of its own). */
  setFlat?(on: boolean): Promise<void>
  /** Set the palette, inversion and colour limits; false when the dataset carries no values. */
  setDisplay?(display: StepDisplay): boolean
  /** Play or hold the dataset's animation. */
  setPlaying?(playing: boolean): void
  setRate?(rate: number): void
  /** AYNI (2026-10-09): these overlays on the globe and no others (two at most; empty: none); false when a layer is not there. */
  setOverlays?(overlays: readonly StepOverlayPlan[]): Promise<boolean>
  /** Play these real-time streams over the dataset, in this order, and no others; resolves with what plays. */
  setRtOverlays?(ids: readonly string[]): Promise<readonly string[]>
  /** AYNI (owner, 2026-10-12): the playing streams' opacity and eye, as the step sets them. */
  setRtLook?(look: readonly StepRtLook[]): void
  /**
   * AYNI (multi-globe plan): show this layout, with these datasets on the
   * other globes (by position from globe 2; null: bare Earth) and each
   * globe's date following globe 1 by its rule.
   */
  setGlobes?(layout: '1' | '2h' | '2v' | '4', others: ReadonlyArray<{ datasetId: string | null; time: GlobeTimeRule }>): Promise<void>
  /** Show a record's pins on the globe (an empty list clears them). */
  showPins?(pins: Array<{ lat: number; lon: number; note?: string; color?: number }>): void
  /** Remove every pin on the globe, the person's own too (a Khipu opens and closes on a globe without pins). */
  clearPins?(): void
  /** What the room sees of this Khipu (a meeting's presenter); null when it closes. */
  setRoomNote?(note: RoomNote | null): void
  /** Put a quiz to the room as a choice question; absent outside a meeting this page presents. */
  askRoom?(): ((quiz: StepQuiz) => void) | null
  openPlans(trigger: HTMLElement | null): void
  announce(message: string): void
  /** Whether the plan keeps Khipus on the account ("Save to my account"). */
  canKeep?(): boolean
  /** Whether downloads are the minimal exchange copy (a plan that keeps no Khipus). */
  exchangeOnly?(): boolean
}

/** AYNI (Situation room, CR3): what the player tells a briefing's recorder. */
export interface BriefingHooks {
  /** A step is on the globe (each time it is shown). */
  onStepShown(index: number, step: KhipuStep): void
  /** The decision written so far on a step ('' none). */
  decision(stepId: string): string
  onDecision(stepId: string, text: string): void
  /** The player closed. */
  onClose?(): void
}

export interface KhipuPlayerHandle {
  close(): void
  isOpen(): boolean
  khipuId(): string | null
  /** The step (path) or moment (log) shown, from 0. */
  position(): number
  playing(): boolean
  togglePlay(): void
  next(): void
  prev(): void
  /** What the headset's Khipu panel shows; the same object until something changed. */
  vrState(): VrKhipuState
  /** Answer the step's quiz (from the headset); ignored once answered. */
  answerQuiz(option: number): void
  /** AYNI (meeting games plan): the Khipu played, and a step to go to (trivia moves through its quiz steps). */
  khipu(): Khipu
  goTo(index: number): void
  /**
   * AYNI (meeting games plan): while the Khipu is played as a trivia game
   * its quizzes are the game's rounds: the panel shows no quiz of its own
   * and does not move on by itself.
   */
  setGameMode(on: boolean): void
}

type DatasetLine =
  | { state: 'idle' }
  | { state: 'loading'; title: string }
  | { state: 'shown'; title: string }
  | { state: 'locked'; title: string; pkg: string }
  | { state: 'unavailable'; title: string }
  | { state: 'failed'; title: string; id: string }
  | { state: 'none' }

const KIND_LABEL: Record<string, string> = {
  story: 'khipu.kind.story',
  session: 'khipu.kind.session',
  briefing: 'khipu.kind.briefing',
  research: 'khipu.kind.research',
  lesson: 'khipu.kind.lesson',
}

const PROMPT_LABEL: Record<string, string> = {
  question: 'lessons.player.prompt.question',
  task: 'lessons.player.prompt.task',
}

const LETTERS = ['A', 'B', 'C', 'D'] // i18n-exempt: option letters

let active: KhipuPlayerHandle | null = null

/**
 * Put a step on the globe once its dataset is there: the camera (or the
 * region), the date (or the first or latest frame, or a point in the
 * video), the playback, the overlay and real-time streams, the colours and, unless `pins` is
 * false, the pins. `current` says whether the step is still the one shown
 * after the overlay's wait. Also used to apply a dataset's opening quietly.
 */
export async function applyStepToGlobe(
  host: KhipuPlayerHost,
  p: StepPlan,
  current: () => boolean = () => true,
  /** `keepFlat`: a dataset's opening, which leaves the flat map or the globe as the viewer chose. */
  opts: { pins?: boolean; lang?: string; keepFlat?: boolean } = {},
  /** AYNI (multi-globe plan): the Khipu, to resolve the other globes' dataset keys; without it they resolve to nothing. */
  k?: Khipu,
): Promise<void> {
  // AYNI (2026-10-19): the flat map or the globe before anything moves the camera: the switch sets one of its own.
  if (p.view && host.setFlat && !opts.keepFlat) {
    await host.setFlat(p.view.flat === true)
    if (!current()) return
  }
  // The spin first: starting it eases the camera, and the step's own move must come after and win.
  if (p.autorotate) host.setAutorotate?.(p.autorotate.on, p.autorotate.speed)
  if (p.view && p.motion && host.playMotion) {
    host.playMotion({
      view: { lat: p.view.lat, lon: p.view.lon, zoom: p.view.zoom, bearing: p.view.bearing ?? 0, pitch: p.view.pitch ?? 0 },
      transition: p.motion.transition, hold: p.motion.hold, effects: p.motion.effects ?? undefined,
      // A step that waits for Next keeps its effects until it is left.
      open: p.advanceSeconds === null,
    })
  } else if (p.view) host.setView(p.view)
  else if (p.region) host.fitRegion?.(p.region)
  if (p.time?.at) host.seekToDate(p.time.at)
  else if (p.time?.relative) host.seekToEdge?.(p.time.relative)
  // Khipu 1.2: the satellites, after the camera (a follow starts from where the step looks).
  if (p.satellites) host.setSatellites?.(p.satellites)
  else if (typeof p.time?.offsetSeconds === 'number') host.seekToOffset?.(p.time.offsetSeconds)
  if (typeof p.time?.rate === 'number') host.setRate?.(p.time.rate)
  if (typeof p.time?.playing === 'boolean') host.setPlaying?.(p.time.playing)
  if (p.overlays && host.setOverlays) {
    await host.setOverlays(p.overlays)
    if (!current()) return
  }
  if (p.realtime && host.setRtOverlays) {
    await host.setRtOverlays(p.realtime)
    if (!current()) return
  }
  if (p.realtimeLook) host.setRtLook?.(p.realtimeLook)
  // AYNI (multi-globe plan): the other globes, each resolved as the step's own dataset is.
  if (p.globes && host.setGlobes) {
    const others = await Promise.all(p.globes.others.map(async (g) => {
      if (!g.datasetKey) return { datasetId: null, time: g.time }
      const resolved = await resolveDatasetKey(k ?? EMPTY_KHIPU, g.datasetKey, opts.lang ?? 'en')
      return { datasetId: resolved.kind === 'none' || resolved.kind === 'unavailable' ? null : resolved.datasetId, time: g.time }
    }))
    if (!current()) return
    await host.setGlobes(p.globes.layout, others)
    if (!current()) return
    // AYNI (Situation room, 2026-10-11): a change of layout makes new globes at the default camera,
    // and the camera is shared, so the step's own view went with it: set it again on the new layout.
    if (p.view && !(p.motion && host.playMotion)) host.setView(p.view)
  }
  if (p.display) host.setDisplay?.(p.display)
  if (p.pins && opts.pins !== false) host.showPins?.(p.pins)
}

/** A Khipu with no datasets, for a plan applied without one (every key then resolves to nothing). */
const EMPTY_KHIPU = { datasets: [] } as unknown as Khipu

/** The open Khipu, if any (opening another replaces it). */
export function activeKhipuPlayer(): KhipuPlayerHandle | null {
  return active
}

export function openKhipuPlayer(
  k: Khipu,
  host: KhipuPlayerHost,
  opts: {
    startAt?: number
    files?: Array<{ name: string; body: string }>
    /** Show Edit (a story or briefing): closes the player and opens the editor at the step shown. */
    onEdit?: (position: number) => void
    /**
     * AYNI (Situation room, CR3): a briefing being recorded. The player says
     * which step is up once it is on the globe, shows the next step's title,
     * and gives a step with a Decision prompt a box for what was decided.
     */
    briefing?: BriefingHooks
  } = {},
): KhipuPlayerHandle {
  active?.close()
  // AYNI (owner, 2026-10-09): a Khipu opens on a globe without pins, and leaves none behind.
  host.clearPins?.()
  const lang = getLocale()
  const steps = k.path.steps
  const logMode = playsLog(k)
  const moments: ReplayMoment[] = logMode ? replayMoments(k.log?.events ?? []) : []
  const total = logMode ? moments.length : steps.length
  let index = -1
  let line: DatasetLine = { state: 'idle' }
  let plan: StepPlan | null = null
  let caption = ''
  let folded = false
  let saveStatus: { text: string; ok: boolean } | null = null
  /** The quiz option chosen on this step, whether its hint shows, and whether it went to the room. */
  let quizChoice: number | null = null
  /** AYNI (meeting games plan): played as a trivia game (see the handle's setGameMode). */
  let gameMode = false
  let hintShown = false
  let askedRoom = false
  let generation = 0
  // A log replays by itself; a path plays when some step moves on by itself.
  let isPlaying = logMode || hasTimedSteps(k)
  const offersPlay = isPlaying
  // AYNI (owner, 2026-11-03: playlists retire for Khipus): a path may start again after its last step.
  const loops = !logMode && total > 1 && khipuLoops(k)
  /** The step after this one: the first again at the end of a path that loops. */
  const after = (i: number): number => (loops && i >= total - 1 ? 0 : i + 1)

  const clock = createAdvanceClock(() => {
    if (index < total - 1 || loops) void go(after(index), true)
    else {
      isPlaying = false
      render()
      host.announce(t('khipu.player.ended'))
    }
  })

  const panel = document.createElement('section')
  panel.id = 'khipu-player'
  panel.className = 'lesson-player khipu-player'
  panel.setAttribute('aria-label', t('khipu.player.aria', { title: localized(k.title, lang) }))
  document.body.appendChild(panel)

  const credit = k.attribution ?? k.creator ?? ''
  const licence = k.license ? (k.license.name ?? k.license.spdx ?? '') : ''

  function datasetHtml(): string {
    switch (line.state) {
      case 'idle':
        return ''
      case 'none':
        return `<p class="lesson-dataset">${escapeHtml(t('lessons.player.dataset.none'))}</p>`
      case 'loading':
        return `<p class="lesson-dataset" aria-busy="true">${escapeHtml(t('lessons.player.dataset.loading', { title: line.title }))}</p>`
      case 'shown':
        return `<p class="lesson-dataset"><span class="lesson-dataset-name">${escapeHtml(line.title)}</span></p>`
      case 'locked':
        return `<p class="lesson-dataset lesson-dataset-locked"><span aria-hidden="true">&#x1F512;&#xFE0E;</span> ${escapeHtml(t('lessons.player.dataset.locked', { title: line.title, package: line.pkg }))} <button type="button" class="lesson-link khipu-plans">${escapeHtml(t('lessons.plans'))}</button></p>`
      case 'unavailable':
        return `<p class="lesson-dataset lesson-dataset-missing">${escapeHtml(t('lessons.player.dataset.unavailable', { title: line.title }))}</p>`
      case 'failed':
        return `<p class="lesson-dataset lesson-dataset-missing">${escapeHtml(t('lessons.player.dataset.failed', { title: line.title }))} <button type="button" class="lesson-link khipu-again">${escapeHtml(t('lessons.player.dataset.again'))}</button></p>`
    }
  }

  function mediaHtml(p: StepPlan): string {
    if (!p.media.length) return ''
    const items = p.media.map((m) => {
      const label = m.title || m.caption || m.url
      const foot = [m.caption && m.caption !== label ? m.caption : '', m.credit ? t('khipu.player.mediaCredit', { credit: m.credit }) : ''].filter(Boolean).join(' · ')
      const link = (text: string) => `<a class="khipu-media-link" href="${escapeAttr(m.url)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeAttr(t('khipu.player.newTab', { title: text }))}">${escapeHtml(text)} <span aria-hidden="true">&#x2197;</span></a>`
      if (m.kind === 'image') {
        return `<figure class="khipu-media"><img src="${escapeAttr(m.url)}" alt="${escapeAttr(m.title || m.caption || '')}" loading="lazy" referrerpolicy="no-referrer">${foot || m.title ? `<figcaption>${escapeHtml([m.title, foot].filter(Boolean).join(' · '))}</figcaption>` : ''}</figure>`
      }
      if (m.kind === 'video') {
        return `<figure class="khipu-media"><video src="${escapeAttr(m.url)}" controls preload="metadata" playsinline></video>${foot || m.title ? `<figcaption>${escapeHtml([m.title, foot].filter(Boolean).join(' · '))}</figcaption>` : ''}</figure>`
      }
      if (m.kind === 'panorama') return `<p class="khipu-media-line">${link(m.title || t('khipu.player.panorama'))}${foot ? ` <span class="khipu-media-foot">${escapeHtml(foot)}</span>` : ''}</p>`
      return `<p class="khipu-media-line">${link(label)}${foot ? ` <span class="khipu-media-foot">${escapeHtml(foot)}</span>` : ''}</p>`
    })
    return `<div class="khipu-media-list">${items.join('')}</div>`
  }

  function verdictText(q: StepQuiz): string {
    return quizChoice === q.correctIndex
      ? t('khipu.quiz.right')
      : t('khipu.quiz.wrong', { answer: `${LETTERS[q.correctIndex]}. ${q.options[q.correctIndex]}` })
  }

  function quizHtml(q: StepQuiz): string {
    const answered = quizChoice !== null
    const options = q.options.map((o, i) => {
      const state = !answered ? '' : i === q.correctIndex ? ' khipu-quiz-right' : i === quizChoice ? ' khipu-quiz-wrong' : ''
      return `<li><button type="button" class="khipu-quiz-option${state}" data-option="${i}" aria-pressed="${String(quizChoice === i)}"${answered ? ' disabled' : ''}><span class="khipu-quiz-letter" aria-hidden="true">${LETTERS[i]}</span> ${escapeHtml(o)}</button></li>`
    }).join('')
    const ask = host.askRoom?.()
    return `<div class="khipu-quiz" role="group" aria-label="${escapeAttr(t('khipu.quiz.aria'))}">
      <p class="khipu-quiz-question"><span class="lesson-prompt-kind">${escapeHtml(t('khipu.quiz.kind'))}</span> ${escapeHtml(q.question)}</p>
      <ol class="khipu-quiz-options">${options}</ol>
      ${answered ? `<p class="khipu-quiz-verdict" role="status" tabindex="-1">${escapeHtml(verdictText(q))}</p>` : ''}
      ${!answered && q.hint ? (hintShown ? `<p class="khipu-quiz-hint">${escapeHtml(q.hint)}</p>` : `<button type="button" class="lesson-link khipu-quiz-hint-btn">${escapeHtml(t('khipu.quiz.hint'))}</button>`) : ''}
      ${ask ? `<button type="button" class="lesson-link khipu-quiz-room"${askedRoom ? ' disabled' : ''}>${escapeHtml(t(askedRoom ? 'khipu.quiz.asked' : 'khipu.quiz.askRoom'))}</button>` : ''}
    </div>`
  }

  function bodyHtml(): string {
    if (index < 0) return ''
    if (logMode) {
      return `${datasetHtml()}${caption ? `<p class="khipu-caption" aria-live="polite">${escapeHtml(caption)}</p>` : ''}
        <label class="khipu-scrub-label"><span class="sr-only">${escapeHtml(t('khipu.player.scrub'))}</span>
          <input type="range" class="khipu-scrub" min="0" max="${Math.max(0, total - 1)}" step="1" value="${index}" aria-valuetext="${escapeAttr(t('khipu.player.moment', { n: index + 1, total }))}">
        </label>`
    }
    const step = steps[index]
    const p = plan
    if (!p) return ''
    const title = localized(step.title, lang) || t('khipu.player.untitledStep', { n: index + 1 })
    // AYNI (Situation room, CR3): a briefing's Decision box, and the step that comes next.
    const decisionAsk = opts.briefing ? decisionPromptOf(step) : null
    const decisionText = decisionAsk ? localized(decisionAsk, lang) : ''
    const notes = decisionAsk ? p.notes.filter((n) => n !== decisionText) : p.notes
    const nextStep = opts.briefing && index < steps.length - 1 ? steps[index + 1] : null
    const decisionHtml = decisionAsk && opts.briefing
      ? `<label class="khipu-decision"><span class="khipu-decision-label">${escapeHtml(decisionText || t('situation.decision.label'))}</span><textarea class="khipu-decision-text" rows="4" maxlength="2000" data-step="${escapeAttr(step.id)}">${escapeHtml(opts.briefing.decision(step.id))}</textarea><span class="khipu-decision-note">${escapeHtml(t('situation.decision.note'))}</span></label>`
      : ''
    return `<h3 class="lesson-step-title">${escapeHtml(title)}</h3>
      ${datasetHtml()}
      ${p.captions.length ? `<div class="khipu-captions" aria-live="polite">${p.captions.map((c) => `<p class="khipu-caption">${PROMPT_LABEL[c.kind] ? `<span class="lesson-prompt-kind">${escapeHtml(t(PROMPT_LABEL[c.kind] as Parameters<typeof t>[0]))}</span> ` : ''}${escapeHtml(c.text)}</p>`).join('')}</div>` : ''}
      ${p.quiz && !gameMode ? quizHtml(p.quiz) : p.quiz ? `<p class="lesson-notice">${escapeHtml(t('game.trivia.inGame'))}</p>` : ''}
      ${mediaHtml(p)}
      ${notes.length ? `<details class="lesson-notes"${opts.briefing ? ' open' : ''}><summary>${escapeHtml(t('khipu.player.notes'))}</summary><ul>${notes.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul></details>` : ''}
      ${decisionHtml}
      ${nextStep ? `<p class="khipu-next-step">${escapeHtml(t('situation.briefing.next', { title: localized(nextStep.title, lang) || t('khipu.player.untitledStep', { n: index + 2 }) }))}</p>` : ''}`
  }

  /** Bumped by every redraw: the headset's panel redraws when it changes. */
  let revision = 0
  let vrCache: { revision: number; state: VrKhipuState } | null = null

  function datasetText(): string {
    switch (line.state) {
      case 'loading': return t('lessons.player.dataset.loading', { title: line.title })
      case 'shown': return line.title
      case 'locked': return t('lessons.player.dataset.locked', { title: line.title, package: line.pkg })
      case 'unavailable': return t('lessons.player.dataset.unavailable', { title: line.title })
      case 'failed': return t('lessons.player.dataset.failed', { title: line.title })
      case 'none': return t('lessons.player.dataset.none')
      default: return ''
    }
  }

  /** The quiz as lines of text, for the headset: the question, and the verdict once answered (the answers are its buttons). */
  function quizLines(): string[] {
    const q = plan?.quiz
    if (!q) return []
    return [`${t('khipu.quiz.kind')}: ${q.question}`, ...(quizChoice !== null ? [verdictText(q)] : [])]
  }

  function answerQuiz(option: number): void {
    const q = plan?.quiz
    if (!q || quizChoice !== null || !Number.isInteger(option) || option < 0 || option >= q.options.length) return
    quizChoice = option
    render()
    host.announce(verdictText(q))
  }

  /** What the room sees of the step: the Khipu's title, the step's, its captions and its pins. */
  function roomNote(): RoomNote | null {
    if (index < 0) return null
    const title = localized(k.title, lang)
    if (logMode) {
      const state = replayStateAt(moments, index, lang, k.datasets)
      return { title, step: '', lines: caption ? [caption] : [], pins: state.pins.map((p, i) => ({ lat: p.lat, lon: p.lon, note: p.note ?? '', color: i % 4 })) }
    }
    return {
      title,
      step: localized(steps[index].title, lang),
      lines: (plan?.captions ?? []).map((c) => c.text),
      pins: (plan?.pins ?? []).map((p) => ({ lat: p.lat, lon: p.lon, note: p.note, color: p.color })),
    }
  }

  function vrState(): VrKhipuState {
    if (vrCache?.revision === revision) return vrCache.state
    const kind = KIND_LABEL[k.kind] ? t(KIND_LABEL[k.kind] as Parameters<typeof t>[0]) : ''
    const progress = index < 0 ? '' : t(logMode ? 'khipu.player.moment' : 'khipu.player.step', { n: index + 1, total })
    const step = !logMode && index >= 0 ? steps[index] : null
    const lines = logMode
      ? (caption ? [caption] : [])
      : [...(plan?.captions ?? []).map((c) => (PROMPT_LABEL[c.kind] ? `${t(PROMPT_LABEL[c.kind] as Parameters<typeof t>[0])}: ${c.text}` : c.text)), ...quizLines()]
    const state: VrKhipuState = {
      title: localized(k.title, lang),
      progress: [kind, progress].filter(Boolean).join(' · '),
      heading: step ? localized(step.title, lang) || t('khipu.player.untitledStep', { n: index + 1 }) : '',
      dataset: datasetText(),
      lines,
      media: logMode ? 0 : plan?.media.length ?? 0,
      canPrev: index > 0,
      isLast: index >= total - 1 && !loops,
      offersPlay,
      playing: isPlaying,
      ...(!logMode && plan?.quiz && !gameMode ? { quiz: { options: plan.quiz.options, chosen: quizChoice, correct: plan.quiz.correctIndex } } : {}),
    }
    vrCache = { revision, state }
    return state
  }

  function render(): void {
    revision++
    const last = index >= total - 1 && !loops
    const progress = index < 0 ? '' : t(logMode ? 'khipu.player.moment' : 'khipu.player.step', { n: index + 1, total })
    const kind = KIND_LABEL[k.kind] ? t(KIND_LABEL[k.kind] as Parameters<typeof t>[0]) : ''
    panel.classList.toggle('folded', folded)
    panel.innerHTML = `
      <div class="lesson-head">
        <button type="button" class="lesson-icon lesson-grip" aria-label="${escapeAttr(t('khipu.player.move'))}" title="${escapeAttr(t('khipu.player.move'))}">&#x283F;</button>
        <div class="lesson-head-text">
          <span class="lesson-title">${escapeHtml(localized(k.title, lang))}</span>
          <span class="lesson-progress">${escapeHtml([kind, progress].filter(Boolean).join(' · '))}</span>
        </div>
        <button type="button" class="lesson-icon lesson-fold" aria-expanded="${String(!folded)}" aria-label="${escapeAttr(t(folded ? 'khipu.player.expand' : 'khipu.player.collapse'))}">${folded ? '&#x25BE;' : '&#x25B4;'}</button>
        <button type="button" class="lesson-icon lesson-close" aria-label="${escapeAttr(t('khipu.player.close'))}">&#x2715;</button>
      </div>
      <div class="lesson-body"${folded ? ' hidden' : ''}>
        ${bodyHtml()}
        <div class="lesson-nav khipu-nav">
          <button type="button" class="lesson-btn lesson-btn-quiet khipu-prev"${index <= 0 ? ' disabled' : ''}>${escapeHtml(t('lessons.player.prev'))}</button>
          ${offersPlay ? `<button type="button" class="lesson-btn lesson-btn-quiet khipu-play" aria-pressed="${String(isPlaying)}">${escapeHtml(t(isPlaying ? 'khipu.player.pause' : 'khipu.player.play'))}</button>` : ''}
          <button type="button" class="lesson-btn khipu-next">${escapeHtml(t(last ? 'lessons.player.finish' : 'khipu.player.next'))}</button>
        </div>
        <div class="lesson-links">
          <button type="button" class="lesson-link khipu-download">${escapeHtml(t('khipu.player.download'))}</button>
          ${opts.onEdit && !logMode ? `<button type="button" class="lesson-link khipu-edit">${escapeHtml(t('khipu.player.edit'))}</button>` : ''}
          ${host.film && !logMode ? `<button type="button" class="lesson-link khipu-film">${escapeHtml(t('khipu.player.film'))}</button>` : ''}
          ${host.canKeep?.() ? `<button type="button" class="lesson-link khipu-save">${escapeHtml(t('lessons.player.save'))}</button>` : ''}
        </div>
        ${saveStatus ? `<p class="lesson-save-status${saveStatus.ok ? '' : ' lesson-save-error'}" role="status">${escapeHtml(saveStatus.text)}</p>` : ''}
        ${credit || licence ? `<p class="lesson-notice khipu-credit">${escapeHtml([credit ? t('khipu.player.credit', { credit }) : '', licence ? t('khipu.player.licence', { licence }) : ''].filter(Boolean).join(' · '))}</p>` : ''}
      </div>`
  }

  /** Load a dataset by its key in the Khipu, saying how it went on the dataset line. */
  async function showDataset(key: string | null | undefined, gen: number, move: LiveDataMove | null = null): Promise<void> {
    if (key === undefined) return
    if (key === null) {
      host.clearDataset?.()
      line = { state: 'none' }
      return
    }
    const resolved = await resolveDatasetKey(k, key, lang)
    if (gen !== generation) return
    if (resolved.kind === 'none') {
      line = { state: 'none' }
      return
    }
    if (resolved.kind === 'unavailable') {
      line = { state: 'unavailable', title: resolved.title }
      return
    }
    const { datasetId: id, title } = resolved
    if (host.currentDatasetId() === id) {
      line = { state: 'shown', title }
      return
    }
    line = { state: 'loading', title }
    render()
    if (id.startsWith('LIB_') && !(await ensureLibraryDataset(id))) {
      if (gen === generation) line = { state: 'unavailable', title }
      return
    }
    const pkg = await host.streamLock(id)
    if (gen !== generation) return
    if (pkg) {
      line = { state: 'locked', title, pkg }
      return
    }
    const ok = move && host.moveData
      ? await host.moveData(id, move, () => host.loadDataset(id), () => gen === generation)
      : await host.loadDataset(id)
    if (gen !== generation) return
    line = ok ? { state: 'shown', title } : { state: 'failed', title, id }
  }

  /** Put what the step decides on the globe, after its dataset. */
  async function applyPlan(p: StepPlan, gen: number): Promise<void> {
    const move = host.moveData ? liveDataMoveOf(p) : null
    if (move) {
      // A data move runs while the camera arrives: the camera first, then the dataset changes, then the rest of the step on it.
      // The other globes wait for the rest of the step (multi-globe plan).
      await applyStepToGlobe(host, { ...p, time: null, display: null, pins: null, overlays: null, realtime: null, globes: null }, () => gen === generation, { pins: false })
      await showDataset(p.datasetKey, gen, move)
      if (gen !== generation) return
      await applyStepToGlobe(host, { ...p, view: null, region: null, autorotate: null }, () => gen === generation, { lang }, k)
      return
    }
    await showDataset(p.datasetKey, gen)
    if (gen !== generation) return
    await applyStepToGlobe(host, p, () => gen === generation, { lang }, k)
  }

  /** Apply one replayed event on top of what the globe shows. */
  async function applyMoment(i: number, gen: number): Promise<void> {
    const state = replayStateAt(moments, i, lang, k.datasets)
    const event = moments[i].event
    caption = state.caption
    switch (event.type) {
      case 'dataset':
        await showDataset(state.dataset, gen)
        break
      case 'view':
        if (state.view) host.setView(state.view)
        break
      case 'time':
        if (state.time?.at) host.seekToDate(state.time.at)
        break
      case 'layers':
        if (state.overlays !== undefined) await host.setOverlays?.(state.overlays)
        break
      case 'pin.add':
      case 'pin.remove':
        host.showPins?.(state.pins)
        break
    }
  }

  /** A jump on the scrubber: that moment's whole state, at once. */
  async function applyStateAt(i: number, gen: number): Promise<void> {
    const state = replayStateAt(moments, i, lang, k.datasets)
    caption = state.caption
    await showDataset(state.dataset, gen)
    if (gen !== generation) return
    if (state.view) host.setView(state.view)
    if (state.time?.at) host.seekToDate(state.time.at)
    if (state.overlays !== undefined) await host.setOverlays?.(state.overlays)
    host.showPins?.(state.pins)
  }

  /** Move to step or moment `i`. `inOrder`: the next one while playing (a replay applies just its event). */
  async function go(i: number, inOrder = false): Promise<void> {
    if (i < 0 || i >= total) return
    clock.cancel()
    host.stopMotion?.()
    const gen = ++generation
    const from = index
    index = i
    saveStatus = null
    quizChoice = null
    hintShown = false
    askedRoom = false
    if (logMode) {
      if (inOrder && i === from + 1) await applyMoment(i, gen)
      else await applyStateAt(i, gen)
    } else {
      plan = planStep(k, steps[i], lang)
      render()
      await applyPlan(plan, gen)
    }
    if (gen !== generation) return
    render()
    if (!logMode) opts.briefing?.onStepShown(i, steps[i])
    host.setRoomNote?.(roomNote())
    if (!logMode) {
      const title = localized(steps[i].title, lang)
      host.announce(title ? t('khipu.player.announce', { n: i + 1, title }) : t('khipu.player.step', { n: i + 1, total }))
    }
    schedule()
  }

  /** While playing, count down to the next step or moment. */
  function schedule(): void {
    clock.cancel()
    if (!isPlaying || index >= total - 1) return
    const ms = logMode ? moments[index + 1].waitMs : plan?.advanceSeconds ? plan.advanceSeconds * 1000 : null
    if (ms !== null) clock.start(ms)
  }

  function togglePlay(): void {
    if (!offersPlay) return
    isPlaying = !isPlaying
    if (isPlaying) {
      if (clock.remaining() !== null) clock.resume()
      else if (index >= total - 1) void go(0)
      else schedule()
    } else {
      clock.pause()
    }
    render()
    panel.querySelector<HTMLElement>('.khipu-play')?.focus()
    host.announce(t(isPlaying ? 'khipu.player.playing' : 'khipu.player.paused'))
  }

  async function save(trigger: HTMLElement): Promise<void> {
    trigger.setAttribute('aria-busy', 'true')
    saveStatus = { text: t('lessons.player.saving'), ok: true }
    render()
    const result = await saveKhipu(k, opts.files ?? [])
    saveStatus = result.ok
      ? { text: t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) }), ok: true }
      : { text: t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed'), ok: false }
    render()
    host.announce(saveStatus.text)
  }

  async function download(trigger: HTMLElement): Promise<void> {
    trigger.setAttribute('aria-busy', 'true')
    try {
      const { blob, filename } = await zipKhipu(k, opts.files ?? [], { exchange: host.exchangeOnly?.() ?? false })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      host.announce(t('lessons.player.downloaded', { filename }))
    } finally {
      trigger.removeAttribute('aria-busy')
    }
  }

  const floating = makeFloating(panel, { headSelector: '.lesson-head', gripClass: 'lesson-grip' })

  panel.addEventListener('click', (e) => {
    const button = (e.target as HTMLElement).closest<HTMLElement>('button')
    if (!button) return
    if (button.classList.contains('lesson-close')) return handle.close()
    if (button.classList.contains('lesson-fold')) {
      folded = !folded
      render()
      floating.keepOnScreen()
      panel.querySelector<HTMLElement>('.lesson-fold')?.focus()
      return
    }
    if (button.classList.contains('khipu-prev')) return handle.prev()
    if (button.classList.contains('khipu-next')) {
      if (index >= total - 1 && !loops) return handle.close()
      return handle.next()
    }
    if (button.classList.contains('khipu-play')) return togglePlay()
    if (button.classList.contains('khipu-plans')) return host.openPlans(button)
    if (button.classList.contains('khipu-again') && line.state === 'failed') return void go(index)
    if (button.classList.contains('khipu-quiz-option') && plan?.quiz && quizChoice === null) {
      answerQuiz(Number(button.dataset.option))
      panel.querySelector<HTMLElement>('.khipu-quiz-verdict')?.focus()
      return
    }
    if (button.classList.contains('khipu-quiz-hint-btn')) {
      hintShown = true
      render()
      panel.querySelector<HTMLElement>('.khipu-quiz-option')?.focus()
      return
    }
    if (button.classList.contains('khipu-quiz-room') && plan?.quiz && !askedRoom) {
      const ask = host.askRoom?.()
      if (!ask) return
      ask(plan.quiz)
      askedRoom = true
      render()
      host.announce(t('khipu.quiz.asked'))
      return
    }
    if (button.classList.contains('khipu-download')) return void download(button)
    if (button.classList.contains('khipu-edit') && opts.onEdit) {
      const at = Math.max(0, index)
      handle.close()
      return opts.onEdit(at)
    }
    if (button.classList.contains('khipu-save')) return void save(button)
    if (button.classList.contains('khipu-film') && host.film) {
      handle.close()
      return host.film(k)
    }
  })
  panel.addEventListener('input', (e) => {
    const target = e.target as HTMLTextAreaElement
    if (!target.classList.contains('khipu-decision-text') || !opts.briefing) return
    opts.briefing.onDecision(target.dataset.step ?? '', target.value)
  })
  panel.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement
    if (!target.classList.contains('khipu-scrub')) return
    void go(Number(target.value))
  })

  const handle: KhipuPlayerHandle = {
    close: () => {
      generation++
      clock.cancel()
      host.stopMotion?.()
      host.showPins?.([])
      host.clearPins?.()
      host.setRoomNote?.(null)
      floating.leave()
      if (active === handle) active = null
      opts.briefing?.onClose?.()
    },
    isOpen: () => isPanelShown(panel),
    khipuId: () => k.id,
    position: () => index,
    playing: () => isPlaying,
    togglePlay,
    next: () => void go(after(index), true),
    prev: () => void go(index - 1),
    vrState,
    answerQuiz,
    khipu: () => k,
    goTo: (i) => void go(i),
    setGameMode: (on) => {
      if (gameMode === on) return
      gameMode = on
      if (on && isPlaying) togglePlay()
      render()
    },
  }
  active = handle
  render()
  void go(Math.min(Math.max(0, opts.startAt ?? 0), Math.max(0, total - 1)))
  return handle
}
